import React, { createContext, useState, useMemo, useCallback, ReactNode, useContext } from 'react';

interface PhotoFilters {
  profileCompletenessFilterValue: boolean | null;
  hasNonPublishedPhotosFilter: boolean;
  onToggleNonPublishedPhotosFilter: () => void;
  onProfileCompletenessFilterChange: (value: boolean) => void;
}

// Default value for context to avoid `undefined` errors
const PhotoFiltersContext = createContext<PhotoFilters>({
  hasNonPublishedPhotosFilter: false,
  profileCompletenessFilterValue: null,
  onToggleNonPublishedPhotosFilter: () => {},
  onProfileCompletenessFilterChange: () => {},
});

const PhotoFiltersContextProvider = ({ children }: { children: ReactNode }) => {
  const [photoFilters, setPhotoFilters] = useState<Pick<PhotoFilters, 'profileCompletenessFilterValue' | 'hasNonPublishedPhotosFilter'>>({
    profileCompletenessFilterValue: null,
    hasNonPublishedPhotosFilter: false,
  });

  const onToggleNonPublishedPhotosFilter = useCallback(() => {
    setPhotoFilters((prev) => ({
      ...prev,
      hasNonPublishedPhotosFilter: !prev.hasNonPublishedPhotosFilter,
    }));
  }, []);

  const onProfileCompletenessFilterChange = useCallback((value: boolean) => {
    setPhotoFilters((prev) => ({
      ...prev,
      profileCompletenessFilterValue: value,
    }));
  }, []);

  const filters = useMemo(
    () => ({
      ...photoFilters,
      onToggleNonPublishedPhotosFilter,
      onProfileCompletenessFilterChange,
    }),
    [photoFilters, onToggleNonPublishedPhotosFilter, onProfileCompletenessFilterChange]
  );

  return <PhotoFiltersContext.Provider value={filters}>{children}</PhotoFiltersContext.Provider>;
};

// Custom Hook for safer context usage
const usePhotoFilters = () => {
  const context = useContext(PhotoFiltersContext);
  if (!context) {
    throw new Error('usePhotoFilters must be used within a PhotoFiltersContextProvider');
  }
  return context;
};

export { PhotoFiltersContext, PhotoFiltersContextProvider, usePhotoFilters };
